import { useEffect, useState } from 'react'
import { fetchSmsLogForDeal, SmsLogRow } from '../../services/smsLogService'

/**
 * What this deal's seller has actually been sent.
 *
 * Every text the platform sends is written to `sms_log`, but until now the only
 * way to check one seller's trail was to open the SMS log and match phone
 * numbers by eye. The question people actually ask — "did they get the link?" —
 * belongs on the deal.
 *
 * Delivery is shown as three separate states, because they are three different
 * facts: the carrier accepted it, the carrier confirmed it landed, or the carrier
 * said it did not. "Awaiting receipt" is not a failure; it means nobody has told
 * us yet.
 */

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })

// The trigger names are machine keys. Title-casing them mechanically gives
// "Bos sms sent", which is not what anyone calls it — so the ones that appear
// on a deal are named properly, and anything new falls back to a readable form
// of its key rather than to nothing.
const REASONS: Record<string, string> = {
  BOS_SMS_SENT: 'Vehicle Purchase Agreement to sign',
  BOS_EMAIL_SENT: 'Vehicle Purchase Agreement emailed',
  SIGNED_CONFIRMATION: 'Signed — confirmation',
  DD_DOCUMENTS_REQUESTED: 'Documents requested',
  PAYMENT_SENT: 'Payment sent',
  AR_REMINDER: 'Invoice reminder',
  api_send_sms: 'Sent by hand',
}

const reason = (t: string | null) =>
  (t && REASONS[t]) ||
  (t || 'Message')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^./, (ch) => ch.toUpperCase())

function Outcome({ row }: { row: SmsLogRow }) {
  const failed = row.status === 'failed' || row.delivery_status === 'undelivered'
  const delivered = row.delivery_status === 'delivered'
  const label = failed
    ? row.delivery_status === 'undelivered'
      ? 'Not delivered'
      : 'Failed to send'
    : delivered
      ? 'Delivered'
      : 'Sent — awaiting receipt'
  const colour = failed ? '#b3261e' : delivered ? '#1f6f3f' : '#6b6b6b'
  return <span style={{ color: colour, whiteSpace: 'nowrap' }}>{label}</span>
}

export default function DealSmsHistory({ dealId }: { dealId: string }) {
  const [rows, setRows] = useState<SmsLogRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    setLoading(true)
    fetchSmsLogForDeal(dealId)
      .then((res) => {
        if (!live) return
        setRows(res.rows)
        setError(res.error)
        setLoading(false)
      })
      .catch((e) => {
        if (!live) return
        setError(e?.message || 'Could not read the message history.')
        setLoading(false)
      })
    return () => {
      live = false
    }
  }, [dealId])

  return (
    <div style={{ marginTop: 24 }}>
      <h4 style={{ margin: '0 0 8px', fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6b6b6b' }}>
        Messages sent
      </h4>
      {loading ? (
        <p style={{ margin: 0, color: '#6b6b6b' }}>Loading…</p>
      ) : error ? (
        <p style={{ margin: 0, color: '#b3261e' }}>Could not read the message history: {error}</p>
      ) : rows.length === 0 ? (
        <p style={{ margin: 0, color: '#6b6b6b' }}>Nothing has been texted on this deal yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {rows.map((r) => (
            <li
              key={r.id}
              style={{
                padding: '10px 0',
                borderTop: '1px solid rgba(0,0,0,0.08)',
                display: 'flex',
                gap: 12,
                alignItems: 'baseline',
                flexWrap: 'wrap',
              }}
            >
              <span style={{ minWidth: 110, color: '#6b6b6b', whiteSpace: 'nowrap' }}>{when(r.created_at)}</span>
              <span style={{ minWidth: 130, whiteSpace: 'nowrap' }}>{r.to_number}</span>
              <span style={{ flex: '1 1 240px', minWidth: 200 }}>
                <b>{reason(r.trigger_name)}</b>
                <br />
                <span style={{ color: '#4a4a4a' }}>{r.message}</span>
              </span>
              <Outcome row={r} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
